import { isPublicLinkLive, publicLinkUnavailableReason } from "@repo/shared/types";
import { AlertCircle, CheckCircle2, Copy, Mail, MessageSquare, QrCode } from "lucide-react";
import { useEffect, useRef, useState, useTransition } from "react";
import { Link } from "react-router";
import { QRCodeModal } from "@/components/qr-code-modal";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useConfirm } from "@/hooks/use-confirm";
import { apiErrorMessage, del, post } from "@/lib/api/_client";
import { useSaveClientContact } from "@/lib/hooks/use-client-detail";
import { useInviteRecipient } from "@/lib/hooks/use-flow-instances/use-invite-recipient";
import { useInvalidateInviteSurface } from "@/lib/hooks/use-flow-instances/use-member-access";
import { useThirdPartyFeatures } from "@/lib/hooks/use-third-party-features";
import { useT } from "@/lib/i18n/use-t";
import { MemberLinksSection } from "./invite-client";
import { T } from "./invite-client-modal.i18n";

type InviteClientModalProps = {
  open: boolean;
  onClose: () => void;
  flowInstanceId: string;
  clientId: string;
  clientName: string;
  clientEmail: string;
  clientMobilePhone: string | null;
  publicToken: string | null;
  /**
   * QL-505 security follow-up — non-null means the link is no longer live
   * even though `publicToken` may still be set (a revoke keeps the token for
   * lookup purposes; see `isPublicLinkLive`). The modal must not read a
   * revoked link as "already enabled".
   */
  publicAccessRevokedReason: string | null;
  publicTokenExpiry: string | null;
  onSuccess?: () => void;
};

export function InviteClientModal({
  open,
  onClose,
  flowInstanceId,
  clientId,
  clientName,
  clientEmail: initialEmail,
  clientMobilePhone: initialPhone,
  publicToken: initialToken,
  publicAccessRevokedReason,
  publicTokenExpiry: initialExpiry,
  onSuccess,
}: InviteClientModalProps) {
  const t = useT(T);
  const { recipient } = useInviteRecipient(flowInstanceId);
  const [publicUrl, setPublicUrl] = useState<string | null>(null);
  const [publicToken, setPublicToken] = useState(initialToken);
  // QL-505 security follow-up — a revoke keeps `publicToken` set on the
  // backend (this reason is stamped; an explicit revoke also nulls the expiry), so
  // `publicToken` alone can no longer tell the modal the link is still
  // usable. Mirrors `publicToken`'s own state pattern: seeded from the prop,
  // then updated directly by the generate/revoke handlers below so a click in
  // THIS session reflects immediately, without waiting for a refetch.
  // A reason without a token is not a withdrawn link — there never was one.
  const [revokedReason, setRevokedReason] = useState(
    initialToken ? publicAccessRevokedReason : null,
  );
  const [publicTokenExpiry, setPublicTokenExpiry] = useState(initialExpiry);
  // A link past its expiry — or with no expiry at all — is just as dead: the
  // server refuses to send it, so it also reads as "needs a new link". Same
  // shared rule as the server and the AI share tool.
  // No token and no revoke reason is simply "no link yet".
  const deadReason =
    publicToken || revokedReason
      ? publicLinkUnavailableReason({ publicTokenExpiry, publicAccessRevokedReason: revokedReason })
      : null;
  const isRevoked = !!deadReason;

  // Set public URL on client side only — never for a revoked link, even
  // though its `publicToken` is still present.
  useEffect(() => {
    if (
      isPublicLinkLive({
        publicToken: initialToken,
        publicTokenExpiry: initialExpiry,
        publicAccessRevokedReason,
      }) &&
      typeof window !== "undefined"
    ) {
      setPublicUrl(`${window.location.origin}/p/${initialToken}`);
    }
  }, [initialToken, publicAccessRevokedReason, initialExpiry]);

  // Narrowed to keep `recipient.name`/`.email`/`.phone`/`.role` safely typed
  // wherever this dialog is showing the representative-only warning.
  const representative = recipient?.kind === "representative" ? recipient : null;
  const isRepresentative = !!representative;
  const isMembers = recipient?.kind === "members";

  // The name/e-mail/phone shown and edited — the client's own for `client`
  // (and while the recipient is still loading), the representative's for
  // `representative` (never the client's own contact — QL-505 follow-up).
  const contactName = representative ? representative.name : clientName;
  const contactEmail = representative ? representative.email || "" : initialEmail || "";
  const contactPhone = representative ? representative.phone || "" : initialPhone || "";
  const displayClientName = !recipient
    ? clientName
    : recipient.kind === "client"
      ? recipient.name
      : recipient.clientName;

  // What the client (or representative) has on file. `savedEmail`/`savedPhone`
  // always track the latest fetched value (so a saved number stops counting
  // as an edit even before a refetch lands). The editable `email`/
  // `mobilePhone` form state is seeded once and otherwise left alone for a
  // "client" recipient — a background refetch must not erase an advisor's
  // in-progress edit — but for a representative the fields are read-only, so
  // they always track the fetched value, including the async transition from
  // "" while `recipient` is still loading.
  const [savedEmail, setSavedEmail] = useState(contactEmail);
  const [savedPhone, setSavedPhone] = useState(contactPhone);
  const [email, setEmail] = useState(contactEmail);
  const [mobilePhone, setMobilePhone] = useState(contactPhone);
  useEffect(() => {
    setSavedEmail(contactEmail);
    setSavedPhone(contactPhone);
    if (representative) {
      setEmail(contactEmail);
      setMobilePhone(contactPhone);
    }
  }, [contactEmail, contactPhone, representative]);

  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  const [isPending, startTransition] = useTransition();

  // UI state
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { confirm, ConfirmationDialog } = useConfirm();

  const [activeTab, setActiveTab] = useState<"email" | "sms">("email");
  // SMS invite is hidden when the tenant has Sinch off (the server won't send it).
  const { features: tpFeatures } = useThirdPartyFeatures();
  const sinchEnabled = tpFeatures?.sinchEnabled ?? true;
  const effectiveTab = sinchEnabled ? activeTab : "email";
  const [showQrModal, setShowQrModal] = useState(false);

  // QL-505 security follow-up — a revoked link still has `publicToken` set,
  // so it must ALSO push this into "needs a new link" (Generate button) —
  // token presence alone used to read a revoked link as still enabled.
  const needsToken = !publicToken || isRevoked;
  // Only flag as "needs update" if the value has CHANGED, not just if it's empty.
  // An emptied field is not an edit either: this form never clears the stored
  // contact. A representative's contact is read-only here, so it can never be
  // "changed".
  const emailChanged = !isRepresentative && !!email.trim() && email !== savedEmail;
  const phoneChanged = !isRepresentative && !!mobilePhone.trim() && mobilePhone !== savedPhone;
  // Each tab saves only its own field, so only that field can block sending.
  const needsUpdate = effectiveTab === "email" ? emailChanged : phoneChanged;
  // Read when an auto-close timer fires, so an edit typed after the timer
  // started still counts.
  const hasUnsavedEditRef = useRef(false);
  hasUnsavedEditRef.current = emailChanged || phoneChanged;

  // The modal stays mounted between openings, so closing it is what discards
  // unsaved edits and clears the last banner.
  const handleClose = () => {
    setEmail(savedEmail);
    setMobilePhone(savedPhone);
    setError(null);
    setSuccess(null);
    onClose();
  };

  // Closes the modal a moment after a send or revoke, unless that would drop an
  // unsaved edit on either tab. The modal then stays open on its success banner.
  const closeSoon = () => {
    setTimeout(() => {
      if (!hasUnsavedEditRef.current) handleClose();
    }, 2000);
  };

  // Check if we have valid contact info to send invite
  const hasValidEmail = effectiveTab === "email" && !!email.trim();
  const hasValidPhone = effectiveTab === "sms" && !!mobilePhone.trim();
  // The group's access link is live: generated and not revoked.
  const isLinkLive = !!publicToken && !isRevoked;
  const canSendInvite = isLinkLive && (hasValidEmail || hasValidPhone) && !needsUpdate;

  // QL-505 follow-up — the group's own access link is the prerequisite for
  // member links too, so its generate/revoke also refreshes the member-access
  // list and who the dialog resolves as the recipient.
  const invalidateInviteSurface = useInvalidateInviteSurface(flowInstanceId);
  const saveClientContact = useSaveClientContact();

  const handleGenerateToken = async () => {
    setError(null);
    setSuccess(null);
    setIsGeneratingToken(true);

    try {
      const result = await post<{
        success: boolean;
        error?: string;
        data?: { token: string; url: string; expiry: string };
      }>(`/internal/flow-instances/${flowInstanceId}/public-access`);

      if (!result.success) {
        setError(result.error || t.failedToGenerateLink);
        return;
      }

      if (result.data) {
        setPublicToken(result.data.token);
        setPublicUrl(result.data.url);
        setRevokedReason(null);
        setPublicTokenExpiry(result.data.expiry);
        setSuccess(t.accessLinkGenerated);
        invalidateInviteSurface();
      }
    } catch (err: any) {
      setError(err?.message || t.failedToGenerateLink);
    } finally {
      setIsGeneratingToken(false);
    }
  };

  const handleUpdateClient = async () => {
    setError(null);
    setSuccess(null);

    startTransition(async () => {
      // Never reached for a representative — see `needsUpdate` above — so this
      // always targets the client's OWN record. QL-603 — only the active tab's
      // field is sent. Save is only offered for a non-empty value.
      const field = effectiveTab === "email" ? "email" : "mobilePhone";
      try {
        const stored = await saveClientContact(
          clientId,
          field,
          field === "email" ? email : mobilePhone,
        );
        // The server trims and lowercases the email, so show what it stored;
        // keeping the typed casing leaves the client page showing other data.
        if (field === "email") {
          setEmail(stored);
          setSavedEmail(stored);
        } else {
          setMobilePhone(stored);
          setSavedPhone(stored);
        }
        setSuccess(t.clientInfoUpdated);
        if (onSuccess) onSuccess();
      } catch (err: any) {
        setError(err?.message || t.failedToUpdateClient);
      }
    });
  };

  const handleSendInvite = () => {
    setError(null);
    setSuccess(null);

    if (!isLinkLive) {
      setError(t.generateLinkFirst);
      return;
    }

    if (effectiveTab === "email") {
      if (!email.trim()) {
        setError(t.enterEmailAddress);
        return;
      }

      startTransition(async () => {
        try {
          const result = await post<{
            success: boolean;
            error?: string;
            data?: { sentTo?: string };
          }>(`/internal/flow-instances/${flowInstanceId}/public-access/invite-email`);

          if (!result.success) {
            setError(result.error || t.failedToSendEmail);
            return;
          }

          setSuccess(t.inviteSentTo(result.data?.sentTo || email));
          if (onSuccess) onSuccess();

          closeSoon();
        } catch (err: any) {
          setError(apiErrorMessage(err, t.failedToSendEmail));
        }
      });
    } else {
      if (!mobilePhone.trim()) {
        setError(t.enterPhoneNumber);
        return;
      }

      startTransition(async () => {
        try {
          const result = await post<{
            success: boolean;
            error?: string;
            data?: { sentTo?: string };
          }>(`/internal/flow-instances/${flowInstanceId}/public-access/invite-sms`);

          if (!result.success) {
            setError(result.error || t.failedToSendSms);
            return;
          }

          setSuccess(t.inviteSentTo(result.data?.sentTo || mobilePhone));
          if (onSuccess) onSuccess();

          closeSoon();
        } catch (err: any) {
          // The SMS invite falls back to e-mail when SMS is off for the tenant.
          setError(apiErrorMessage(err, t.failedToSendSms));
        }
      });
    }
  };

  const handleCopyLink = () => {
    if (publicUrl) {
      navigator.clipboard.writeText(publicUrl);
      setSuccess(t.linkCopied);
      setTimeout(() => setSuccess(null), 2000);
    }
  };

  const handleRevokeAccess = async () => {
    const confirmed = await confirm({
      title: t.revokePublicAccessTitle,
      description: t.revokePublicAccessDescription,
      confirmText: t.revokeAccessConfirm,
      cancelText: t.cancel,
      variant: "destructive",
    });

    if (!confirmed) return;

    setError(null);
    setSuccess(null);

    startTransition(async () => {
      try {
        const result = await del<{ success: boolean; error?: string }>(
          `/internal/flow-instances/${flowInstanceId}/public-access`,
        );

        if (!result.success) {
          setError(result.error || t.failedToRevokeAccess);
          return;
        }

        setPublicToken(null);
        setPublicUrl(null);
        setRevokedReason("revoked");
        setSuccess(t.accessRevokedSuccess);
        invalidateInviteSurface();

        if (onSuccess) onSuccess();

        closeSoon();
      } catch (err: any) {
        setError(err?.message || t.failedToRevokeAccess);
      }
    });
  };

  const roleLabel = representative ? t.roleLabels[representative.role] || representative.role : "";
  const representativeEditPath =
    representative?.role === "group_member"
      ? { to: `/clients/${clientId}?tab=members`, hint: t.representativeContactHintMembers }
      : { to: `/clients/${clientId}?tab=parties`, hint: t.representativeContactHintParties };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      {/* An unsaved edit on either tab is lost on close, not just the active one. */}
      <DialogContent className="max-w-2xl" isDirty={emailChanged || phoneChanged}>
        <DialogHeader>
          <DialogTitle>{t.inviteTitle(displayClientName)}</DialogTitle>
          <DialogDescription>
            {isMembers
              ? recipient?.kind === "members" && recipient.basis === "representatives"
                ? t.inviteDescriptionRepresentatives
                : t.inviteDescriptionMembers
              : t.inviteDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isRevoked && (
            <Alert variant="warning">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t.linkNoLongerActive(t.revokeReasonLabels[deadReason ?? ""] ?? deadReason ?? "")}
              </AlertDescription>
            </Alert>
          )}

          {isRepresentative && (
            <Alert variant="warning">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t.representativeWarning({
                  name: contactName,
                  role: roleLabel,
                  clientName: displayClientName,
                })}
              </AlertDescription>
            </Alert>
          )}

          {representative && !representative.email && !representative.phone && (
            <Alert variant={representative.hasSsn ? "warning" : "destructive"} role="alert">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {(representative.hasSsn
                  ? t.representativeBankIdOnly
                  : t.representativeCannotSignIn)(representative.name)}{" "}
                <Link to={representativeEditPath.to} className="underline">
                  {t.openClientPage}
                </Link>
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert variant="success">
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          {/* Step 1: Generate Public Access Link */}
          {needsToken && (
            <div className="p-4 bg-muted/50 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">{t.step1Title}</h4>
                  <p className="text-sm text-muted-foreground">{t.step1Description}</p>
                </div>
                <Button onClick={handleGenerateToken} size="sm" loading={isGeneratingToken}>
                  {t.generateLink}
                </Button>
              </div>
            </div>
          )}

          {/* Show public URL when available */}
          {publicUrl && isMembers && (
            <div className="p-4 bg-muted/50 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <CheckCircle2 className="h-5 w-5 text-success" />
                  <p className="text-sm text-muted-foreground ml-2">
                    {recipient?.kind === "members" && recipient.basis === "representatives"
                      ? t.clientLinkNotForSending
                      : t.groupLinkNotForSending}
                  </p>
                </div>
                <Button size="sm" variant="ghost" onClick={handleRevokeAccess} disabled={isPending}>
                  {t.revokeAccess}
                </Button>
              </div>
            </div>
          )}
          {publicUrl && !isMembers && (
            <div className="p-4 bg-muted/50 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <CheckCircle2 className="h-5 w-5 text-success" />
                  <h4 className="font-medium ml-2">{t.publicAccessLink}</h4>
                </div>
                <Button size="sm" variant="ghost" onClick={handleRevokeAccess} disabled={isPending}>
                  {t.revokeAccess}
                </Button>
              </div>
              <div className="flex gap-2">
                <div className="flex-1 px-3 py-2 bg-background rounded-md text-sm font-mono truncate">
                  {publicUrl}
                </div>
                <Button
                  size="icon"
                  tooltip={t.qrModalTitle}
                  variant="outline"
                  onClick={() => setShowQrModal(true)}
                >
                  <QrCode className="w-4 h-4" />
                </Button>
                <Button
                  data-dialog-guard-ignore
                  size="icon"
                  tooltip={t.copy}
                  variant="outline"
                  onClick={handleCopyLink}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: a group without a representative sends one link per
              member instead of a single e-mail/SMS send. Members are listed
              even before the group link is live, but their links stay
              unavailable until it is. */}
          {recipient?.kind === "members" && (
            <MemberLinksSection
              instanceId={flowInstanceId}
              clientId={clientId}
              groupLinkLive={isLinkLive}
              basis={recipient.basis}
            />
          )}

          {isLinkLive && !isMembers && (
            <Tabs value={effectiveTab} onValueChange={(v) => setActiveTab(v as "email" | "sms")}>
              <TabsList className={`grid w-full ${sinchEnabled ? "grid-cols-2" : "grid-cols-1"}`}>
                <TabsTrigger value="email">
                  <Mail className="mr-2 h-4 w-4" />
                  {t.emailInvite}
                </TabsTrigger>
                {sinchEnabled && (
                  <TabsTrigger value="sms">
                    <MessageSquare className="mr-2 h-4 w-4" />
                    {t.smsInvite}
                  </TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="email" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">
                    {t.clientEmail} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="client@example.com"
                    disabled={isPending || isRepresentative}
                  />
                  {isRepresentative ? (
                    <p className="text-xs text-muted-foreground">
                      {representativeEditPath.hint(contactName)}{" "}
                      <Link to={representativeEditPath.to} className="text-primary hover:underline">
                        {t.openClientPage}
                      </Link>
                    </p>
                  ) : (
                    <>
                      {emailChanged && (
                        <p className="text-xs text-muted-foreground">{t.emailUpdatedNote}</p>
                      )}
                      {!email.trim() && <p className="text-xs text-warning">{t.addEmailNote}</p>}
                    </>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="sms" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="mobilePhone">
                    {t.mobilePhone} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="mobilePhone"
                    type="tel"
                    value={mobilePhone}
                    onChange={(e) => setMobilePhone(e.target.value)}
                    placeholder="+47 XXX XX XXX"
                    disabled={isPending || isRepresentative}
                  />
                  {isRepresentative ? (
                    <p className="text-xs text-muted-foreground">
                      {representativeEditPath.hint(contactName)}{" "}
                      <Link to={representativeEditPath.to} className="text-primary hover:underline">
                        {t.openClientPage}
                      </Link>
                    </p>
                  ) : (
                    <>
                      {phoneChanged && (
                        <p className="text-xs text-muted-foreground">{t.phoneUpdatedNote}</p>
                      )}
                      {!mobilePhone.trim() && (
                        <p className="text-xs text-warning">{t.addPhoneNote}</p>
                      )}
                    </>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </div>

        <DialogFooter className="gap-2">
          <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>

          {needsUpdate && isLinkLive && (
            <Button onClick={handleUpdateClient} loading={isPending} variant="secondary">
              {t.save}
            </Button>
          )}

          {!isMembers && canSendInvite && (
            <Button onClick={handleSendInvite} loading={isPending}>
              {effectiveTab === "email" ? t.sendEmailInvite : t.sendSmsInvite}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
      <ConfirmationDialog />
      {publicUrl && (
        <QRCodeModal
          open={showQrModal}
          onClose={() => setShowQrModal(false)}
          url={publicUrl}
          title={t.qrModalTitle}
          downloadFileName={`client-access-qr-${clientName.replace(/\s+/g, "-").toLowerCase()}`}
        />
      )}
    </Dialog>
  );
}
